import { logger } from "../utils/logger";
import { useState } from 'react';
import { Mail, Send, CheckCircle, AlertCircle, Sparkles, ArrowRight } from 'lucide-react';
import { apiFetch, safeJsonParse } from '../utils/api';
import { toast } from 'sonner';

interface NewsletterSignupProps {
  variant?: 'footer' | 'inline' | 'popup' | 'blog';
  showTitle?: boolean;
}

export function NewsletterSignup({ variant = 'inline', showTitle = true }: NewsletterSignupProps) {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [message, setMessage] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email || !email.includes('@')) {
      setStatus('error');
      setMessage('Please enter a valid email address');
      return;
    }

    setStatus('loading');
    
    try {
      // Submit to backend
      const submissionData = {
        form_type: 'newsletter',
        form_name: 'Newsletter Signup',
        email,
        source: variant,
        timestamp: new Date().toISOString()
      };

      const response = await apiFetch('/forms/submit', {
        method: 'POST',
        body: JSON.stringify(submissionData)
      });

      if (!response.ok) {
        const errorText = await response.text();
        logger.error('❌ [NEWSLETTER] Server error:', errorText);
        throw new Error('Failed to subscribe');
      }
      
      const result = await safeJsonParse(response);

      if (!result.success) {
        throw new Error(result.error || 'Failed to subscribe');
      }

      setStatus('success');
      setMessage('Thanks for subscribing! Check your email to confirm.');
      setEmail('');
      
      // Show toast notification
      toast.success('Successfully subscribed to newsletter!', {
        duration: 4000,
        position: 'top-center'
      });
      
      // Reset after 5 seconds
      setTimeout(() => {
        setStatus('idle');
        setMessage('');
      }, 5000);
      
    } catch (error: any) {
      logger.error('❌ [NEWSLETTER] Error submitting newsletter signup:', error);
      setStatus('error');
      setMessage('Failed to subscribe. Please try again.');
      
      toast.error('Failed to subscribe. Please try again.', {
        duration: 5000,
        position: 'top-center'
      });
      
      // Reset error after 5 seconds
      setTimeout(() => {
        setStatus('idle');
        setMessage('');
      }, 5000);
    }
  };

  // Footer variant - compact horizontal form
  if (variant === 'footer') {
    return (
      <div className="w-full max-w-md">
        {status === 'success' ? (
          <div className="bg-green-500/20 backdrop-blur-xl border border-green-400/30 rounded-xl px-4 py-3 flex items-center gap-3 animate-in fade-in slide-in-from-bottom-2 duration-300">
            <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0" />
            <p className="text-sm text-green-100">{message}</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="relative">
            <div className="relative">
              {/* Glow effect */}
              <div className="absolute inset-0 bg-gradient-to-r from-[#E87D3E]/20 to-[#E8B84D]/20 rounded-xl blur-xl" />
              
              <div className="relative flex gap-2">
                <div className="relative flex-1">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/50" />
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Your email address"
                    disabled={status === 'loading'}
                    className="w-full bg-white/10 backdrop-blur-xl border border-white/20 rounded-xl pl-10 pr-4 py-3 text-white placeholder:text-white/50 focus:outline-none focus:ring-2 focus:ring-[#E87D3E] focus:border-transparent transition-all text-sm disabled:opacity-50"
                  />
                </div>
                <button
                  type="submit"
                  disabled={status === 'loading'}
                  className="group relative px-6 py-3 bg-gradient-to-r from-[#D74444] to-[#E87D3E] hover:from-[#E87D3E] hover:to-[#E8B84D] text-white rounded-xl font-bold transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-xl flex items-center gap-2"
                >
                  {status === 'loading' ? (
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  ) : (
                    <>
                      <span className="hidden sm:inline">Subscribe</span>
                      <Send className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
                    </>
                  )}
                </button>
              </div>
            </div>
            
            {status === 'error' && (
              <div className="mt-2 flex items-center gap-2 text-red-300 text-xs animate-in fade-in slide-in-from-top-1 duration-200">
                <AlertCircle className="w-3 h-3" />
                <span>{message}</span>
              </div>
            )}
          </form>
        )}
      </div>
    );
  }

  // Inline variant - full featured with title and description
  if (variant === 'inline') {
    return (
      <div className="relative group/newsletter">
        {/* Outer glow */}
        <div className="absolute inset-0 bg-gradient-to-br from-[#D74444]/20 to-[#E87D3E]/20 rounded-3xl blur-2xl opacity-60 group-hover/newsletter:opacity-80 transition-opacity" />
        
        <div className="relative bg-gradient-to-br from-white to-gray-50/50 rounded-3xl p-8 border-2 border-gray-200 shadow-xl">
          {/* Decorative orb */}
          <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-bl from-[#E8B84D]/20 to-transparent rounded-full blur-2xl" />
          
          <div className="relative">
            {showTitle && (
              <div className="mb-6">
                <div className="inline-flex items-center gap-2 bg-gradient-to-r from-[#2F2E5C] to-[#4a487a] text-white px-4 py-2 rounded-full text-sm font-bold mb-4">
                  <Sparkles className="w-4 h-4" />
                  <span>Stay Informed</span>
                </div>
                <h3 className="text-2xl font-bold text-[#2F2E5C] mb-2">
                  Subscribe to Our Newsletter
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  Get exclusive insights, case studies, and animation tips delivered to your inbox monthly.
                </p>
              </div>
            )}

            {status === 'success' ? (
              <div className="bg-gradient-to-r from-green-500 to-green-600 rounded-2xl p-6 text-white flex items-start gap-4 animate-in fade-in slide-in-from-bottom-4 duration-500">
                <div className="w-12 h-12 bg-white/20 backdrop-blur-xl rounded-full flex items-center justify-center flex-shrink-0">
                  <CheckCircle className="w-6 h-6" />
                </div>
                <div>
                  <h4 className="font-bold mb-1">Success! 🎉</h4>
                  <p className="text-sm text-white/90">{message}</p>
                </div>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-3">
                <div className="relative">
                  <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Enter your email address"
                    disabled={status === 'loading'}
                    className="w-full bg-white border-2 border-gray-200 rounded-xl pl-12 pr-4 py-4 text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-[#D74444] focus:border-transparent transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  />
                </div>

                {status === 'error' && (
                  <div className="flex items-center gap-2 text-red-600 text-sm bg-red-50 rounded-xl px-4 py-3 border border-red-200 animate-in fade-in slide-in-from-top-2 duration-200">
                    <AlertCircle className="w-4 h-4 flex-shrink-0" />
                    <span>{message}</span>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={status === 'loading'}
                  className="group w-full relative overflow-hidden rounded-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {/* Button glow */}
                  <div className="absolute inset-0 bg-gradient-to-r from-[#E87D3E] to-[#E8B84D] rounded-xl blur-lg opacity-50 group-hover:opacity-75 transition-opacity" />
                  
                  <div className="relative bg-gradient-to-r from-[#D74444] to-[#E87D3E] hover:from-[#E87D3E] hover:to-[#E8B84D] text-white px-8 py-4 rounded-xl transition-all shadow-xl flex items-center justify-center gap-3">
                    {status === 'loading' ? (
                      <>
                        <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        <span className="font-bold">Subscribing...</span>
                      </>
                    ) : (
                      <>
                        <span className="font-bold">Subscribe Now</span>
                        <Send className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                      </>
                    )}
                  </div>
                </button>

                <p className="text-xs text-gray-500 text-center">
                  No spam. Unsubscribe anytime. Read our{' '}
                  <a href="/privacy" className="text-[#D74444] hover:text-[#E87D3E] font-semibold underline">
                    Privacy Policy
                  </a>
                </p>
              </form>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Blog variant - horizontal form for blog post newsletter section
  if (variant === 'blog') {
    return (
      <div className="w-full">
        {status === 'success' ? (
          <div className="bg-green-500/20 backdrop-blur-xl border-2 border-green-400/50 rounded-2xl px-6 py-4 flex items-center gap-3 animate-in fade-in slide-in-from-bottom-2 duration-300">
            <CheckCircle className="w-6 h-6 text-green-300 flex-shrink-0" />
            <p className="text-white font-medium">{message}</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="flex gap-3 flex-col sm:flex-row">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your work email"
                required
                disabled={status === 'loading'}
                className="flex-1 px-6 py-4 rounded-full focus:outline-none focus:ring-2 focus:ring-white text-gray-900 text-base placeholder:text-gray-500 disabled:opacity-50"
              />
              <button
                type="submit"
                disabled={status === 'loading'}
                className="bg-[#D74444] hover:bg-[#c13939] text-white rounded-full px-8 py-4 font-bold text-base whitespace-nowrap transition-all disabled:opacity-50 shadow-lg hover:shadow-xl flex items-center justify-center gap-2"
              >
                {status === 'loading' ? (
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                ) : (
                  <>
                    Subscribe Free
                    <ArrowRight className="w-5 h-5" />
                  </>
                )}
              </button>
            </div>
            
            {status === 'error' && (
              <div className="flex items-center gap-2 text-red-300 text-sm bg-red-900/30 rounded-lg px-4 py-2 border border-red-500/30 animate-in fade-in slide-in-from-top-1 duration-200">
                <AlertCircle className="w-4 h-4" />
                <span>{message}</span>
              </div>
            )}
          </form>
        )}
      </div>
    );
  }

  // Popup variant - simplified for modal use
  return (
    <div className="w-full max-w-md mx-auto">
      {showTitle && (
        <div className="text-center mb-6">
          <div className="inline-flex items-center gap-2 bg-gradient-to-r from-[#2F2E5C] to-[#4a487a] text-white px-4 py-2 rounded-full text-sm font-bold mb-4">
            <Sparkles className="w-4 h-4" />
            <span>Join Our Newsletter</span>
          </div>
          <h3 className="text-xl font-bold text-[#2F2E5C] mb-2">
            Get Industry Insights
          </h3>
          <p className="text-sm text-gray-600">
            Exclusive tips, case studies, and animation trends.
          </p>
        </div>
      )}

      {status === 'success' ? (
        <div className="bg-gradient-to-r from-green-500 to-green-600 rounded-2xl p-6 text-white flex items-center gap-4 animate-in fade-in zoom-in-95 duration-300">
          <CheckCircle className="w-8 h-8 flex-shrink-0" />
          <div>
            <h4 className="font-bold mb-1">Welcome aboard! 🎉</h4>
            <p className="text-sm text-white/90">{message}</p>
          </div>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="relative">
            <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter your email"
              disabled={status === 'loading'}
              className="w-full bg-white border-2 border-gray-200 rounded-xl pl-12 pr-4 py-3.5 text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-[#D74444] focus:border-transparent transition-all disabled:opacity-50"
            />
          </div>

          {status === 'error' && (
            <div className="flex items-center gap-2 text-red-600 text-sm bg-red-50 rounded-lg px-3 py-2 border border-red-200">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              <span>{message}</span>
            </div>
          )}

          <button
            type="submit"
            disabled={status === 'loading'}
            className="w-full bg-gradient-to-r from-[#D74444] to-[#E87D3E] hover:from-[#E87D3E] hover:to-[#E8B84D] text-white px-6 py-3.5 rounded-xl font-bold transition-all disabled:opacity-50 shadow-lg hover:shadow-xl flex items-center justify-center gap-2"
          >
            {status === 'loading' ? (
              <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            ) : (
              <>
                <span>Subscribe</span>
                <Send className="w-4 h-4" />
              </>
            )}
          </button>

          <p className="text-xs text-gray-500 text-center">
            Unsubscribe anytime. Privacy protected.
          </p>
        </form>
      )}
    </div>
  );
}